/**
 * Night Owl's Rive art (src/assets/rive/night-owl-fsm.riv, built from apps/web/rive): the living owl
 * and the sidebar's icons. The file and the runtime load once and every piece of art shares them.
 *
 * Each piece shows its `fallback` (the still SVG it replaces) until it's ready, and keeps showing it
 * for people who ask for less motion, in tests, and if the file or the runtime fails to load, so
 * nothing ever goes missing because an animation didn't.
 */
import { Alignment, Fit, Layout, Rive, RiveFile, RuntimeLoader, StateMachineInputType, type StateMachineInput } from "@rive-app/canvas";
import riveWasmUrl from "@rive-app/canvas/rive.wasm?url";
import { useEffect, useRef, useState, type ReactNode } from "react";
import riveFileUrl from "@/assets/rive/night-owl-fsm.riv?url";

export type RiveArtboard =
  | "owl-mark" | "owl-hero" | "owl-loader"
  | "nav-home" | "nav-calendar" | "nav-clients" | "nav-requests" | "nav-quote" | "nav-job" | "nav-invoice"
  | "nav-finance" | "nav-metrics" | "nav-clock" | "nav-truck" | "nav-book" | "nav-settings" | "nav-chat";

/** Each artboard's state machine (tools/gen.py names them). */
const MACHINE: Record<string, string> = { "owl-mark": "Owl", "owl-hero": "Hero", "owl-loader": "Loader" };
const machineFor = (artboard: RiveArtboard) => MACHINE[artboard] ?? "Icon";

let shared: Promise<RiveFile> | null = null;

/** The .riv, loaded once. Holds a reference of its own so the file outlives any one piece of art. */
function loadFile(): Promise<RiveFile> {
  if (!shared) {
    RuntimeLoader.setWasmUrl(riveWasmUrl);
    shared = new Promise<RiveFile>((resolve, reject) => {
      const file = new RiveFile({
        src: riveFileUrl,
        onLoad: () => {
          file.getInstance();
          resolve(file);
        },
        onLoadError: () => reject(new Error("The Rive file didn't load")),
      });
      file.init().catch(reject);
    });
  }
  return shared;
}

function prefersLessMotion() {
  return typeof window === "undefined" || !window.matchMedia || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Bools set as values; triggers fire when their value changes to a new truthy one (a counter works). */
export type RiveInputs = Record<string, boolean | number | undefined>;

export function RiveArt({ artboard, width, height, inputs = {}, hoverFromParent = false, fallback, className }: {
  artboard: RiveArtboard;
  width: number;
  height: number;
  inputs?: RiveInputs;
  /** Set the `hover` input while the link or button around the art is hovered or has keyboard focus. */
  hoverFromParent?: boolean;
  fallback: ReactNode;
  className?: string;
}) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hovered, setHovered] = useState(false);
  const riveRef = useRef<Rive | null>(null);
  const inputsRef = useRef<Map<string, StateMachineInput>>(new Map());
  const firedRef = useRef<Record<string, boolean | number | undefined>>({});
  const merged = hoverFromParent ? { ...inputs, hover: hovered } : inputs;
  const latest = useRef(merged);
  latest.current = merged;
  const [ready, setReady] = useState(false);
  const [still] = useState(prefersLessMotion);

  useEffect(() => {
    if (still) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    loadFile()
      .then((riveFile) => {
        if (cancelled) return;
        const machine = machineFor(artboard);
        const rive = new Rive({
          riveFile,
          canvas,
          artboard,
          stateMachines: machine,
          autoplay: true,
          layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
          onLoad: () => {
            if (cancelled) return;
            rive.resizeDrawingSurfaceToCanvas();
            for (const input of rive.stateMachineInputs(machine) ?? []) inputsRef.current.set(input.name, input);
            // Triggers fire on a change after this, not for counts that went up before the art loaded.
            for (const [name, value] of Object.entries(latest.current)) {
              if (inputsRef.current.get(name)?.type === StateMachineInputType.Trigger) firedRef.current[name] = value;
            }
            apply(latest.current, inputsRef.current, firedRef.current);
            setReady(true);
          },
          onLoadError: () => setReady(false),
        });
        riveRef.current = rive;
      })
      .catch(() => undefined); // The still art stays.
    return () => {
      cancelled = true;
      riveRef.current?.cleanup();
      riveRef.current = null;
      inputsRef.current.clear();
    };
  }, [artboard, still]);

  // Inputs change with hover, the open section and the theme: pass each new value to the machine.
  useEffect(() => {
    apply(merged, inputsRef.current, firedRef.current);
  });

  useEffect(() => {
    if (!hoverFromParent || still) return;
    const target = wrapRef.current?.closest("a, button");
    if (!target) return;
    const on = () => setHovered(true);
    const off = () => setHovered(false);
    const blur = () => { if (!target.matches(":hover")) off(); };
    const leave = () => { if (!target.contains(document.activeElement) || !target.matches(":focus-visible")) off(); };
    target.addEventListener("pointerenter", on);
    target.addEventListener("pointerleave", leave);
    target.addEventListener("focusin", on);
    target.addEventListener("focusout", blur);
    return () => {
      target.removeEventListener("pointerenter", on);
      target.removeEventListener("pointerleave", leave);
      target.removeEventListener("focusin", on);
      target.removeEventListener("focusout", blur);
    };
  }, [hoverFromParent, still]);

  if (still) return <>{fallback}</>;
  return (
    <span ref={wrapRef} className={className} style={{ position: "relative", display: "inline-grid", width, height, flex: "none" }} aria-hidden="true">
      {!ready && <span style={{ gridArea: "1 / 1", display: "grid", placeItems: "center" }}>{fallback}</span>}
      <canvas ref={canvasRef} width={width} height={height} style={{ gridArea: "1 / 1", width, height, opacity: ready ? 1 : 0 }} />
    </span>
  );
}

function apply(values: RiveInputs, inputs: Map<string, StateMachineInput>, fired: Record<string, boolean | number | undefined>) {
  for (const [name, value] of Object.entries(values)) {
    const input = inputs.get(name);
    if (!input || value === undefined) continue;
    if (input.type === StateMachineInputType.Trigger) {
      if (value && value !== fired[name]) input.fire();
      fired[name] = value;
    } else if (input.value !== value) {
      input.value = value;
    }
  }
}
